import React, { useState } from 'react'
import { interceptExternalLinkClick } from '../../lib/nativeShell'
import { motion } from 'motion/react'
import { X, Edit2, Maximize2 } from 'lucide-react'
import { collectionsApi } from '../../services/collectionsApi'
import type { CollectionWithItems } from '../../types/collections'
import { useCollectionItem } from '../../hooks/useCollectionItem'
import ImageLightbox from './ImageLightbox'
import EditCollectionItemById from './EditCollectionItemById'

interface CollectionItemModalProps {
  collectionId: number
  itemId: string | number
  onClose: () => void
  onSuccess?: () => void
  onRefresh?: () => void
}

const CollectionItemModal: React.FC<CollectionItemModalProps> = ({
  collectionId,
  itemId,
  onClose,
  onSuccess,
  onRefresh,
}) => {
  const [editing, setEditing] = useState(false)
  const [lightboxField, setLightboxField] = useState<string | null>(null)

  const [collection, setCollection] = useState<CollectionWithItems | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { item } = useCollectionItem(collectionId, String(itemId))

  React.useEffect(() => {
    const loadCollection = async () => {
      try {
        const data = await collectionsApi.get(collectionId, true)
        setCollection(data)
      } catch (err) {
        console.error('Failed to load collection:', err)
        setError('Failed to load collection')
      } finally {
        setLoading(false)
      }
    }

    loadCollection()
  }, [collectionId])

  const renderFieldValue = (fieldName: string, field: any) => {
    const value = item?.data?.[fieldName]

    if (!value && value !== 0 && value !== false) {
      return <span className="text-gray-400">—</span>
    }

    switch (field.type) {
      case 'boolean':
        return <span>{value ? 'Yes' : 'No'}</span>

      case 'picture':
        return value ? (
          <div className="relative inline-block">
            <button
              type="button"
              onClick={() => setLightboxField(fieldName)}
              className="block group focus:outline-none"
              aria-label="View full image"
            >
              <img
                src={value}
                alt={fieldName}
                className="max-w-xs h-32 object-contain bg-gray-50 border border-gray-200 rounded-lg group-hover:opacity-90 transition-opacity cursor-zoom-in"
              />
            </button>
            <motion.button
              type="button"
              onClick={() => setLightboxField(fieldName)}
              className="absolute top-1.5 right-1.5 p-1 bg-black/40 hover:bg-black/60 text-white rounded-md backdrop-blur-sm"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              aria-label="View full image"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </motion.button>
          </div>
        ) : (
          <span className="text-gray-400">No image</span>
        )

      case 'url':
        return (
          <a href={value} target="_blank" rel="noopener noreferrer" onClick={interceptExternalLinkClick} className="text-amber-800 hover:underline">
            {value}
          </a>
        )

      case 'email':
        return (
          <a href={`mailto:${value}`} className="text-amber-800 hover:underline">
            {value}
          </a>
        )

      default:
        return <span>{value}</span>
    }
  }

  if (loading || !collection || !item) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6">
          {error ? (
            <div className="text-rose-600">{error}</div>
          ) : (
            <div className="animate-spin h-8 w-8 border-4 border-amber-600 border-t-transparent rounded-full" />
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="fixed inset-0 bg-white/75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-xl border border-gray-300">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold">
              {collection.name}
            </h2>
            <div className="flex items-center gap-2">
              {!(collection.system_kind === 'places' && item.data?.source === 'google') && (
                <button
                  onClick={() => setEditing(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {Object.entries(collection.config.fields)
              .sort(([, a], [, b]) => (a.position ?? 0) - (b.position ?? 0))
              .map(([fieldName, field]) => (
                <div key={fieldName}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {fieldName}
                  </label>
                  <div className="text-gray-900">
                    {renderFieldValue(fieldName, field)}
                  </div>
                </div>
              ))}
          </div>
        </div>
        {lightboxField && item.data?.[lightboxField] && (
          <ImageLightbox
            src={item.data[lightboxField]}
            alt={lightboxField}
            onClose={() => setLightboxField(null)}
          />
        )}
      </div>
      {editing && (
        <EditCollectionItemById
          collectionId={collectionId}
          itemId={itemId}
          onClose={() => setEditing(false)}
          onSuccess={() => {
            setEditing(false)
            onSuccess?.()
            onRefresh?.()
          }}
        />
      )}
    </>
  )
}

export default CollectionItemModal
